import { createContext, useState, useContext, useEffect } from 'react';
import { ROLE_PERMISSIONS } from '../config/roles';
import axios from 'axios';

// Create axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true,
});

// Add request interceptor to include credentials
api.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// List of public routes that don't require authentication
const publicRoutes = [
  '/login',
  '/create-account',
  '/forgot-password',
  '/reset-password',
  '/',
  '/vilkår',
  '/personvern'
];

// Check if current path is a public route
const isPublicRoute = (path) => {
  return publicRoutes.some(route => path === route || path.startsWith(route + '/'));
};

// Add response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const currentPath = window.location.pathname;
    const requestUrl = originalRequest?.url || '';

    // Don't try to refresh token for public routes, refresh-token endpoint, or if already retried
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Skip token refresh for public routes
      if (isPublicRoute(currentPath)) {
        return Promise.reject(error);
      }

      // Skip token refresh for the refresh-token endpoint itself to avoid infinite loops
      if (requestUrl.includes('/api/auth/refresh-token')) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        // Try to refresh the token
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh-token`,
          {},
          { withCredentials: true }
        );

        if (response.data.success) {
          // Retry the original request
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed - this is okay if user doesn't have refresh token
        // Only log if it's not a "no refresh token" error (401)
        if (refreshError.response?.status !== 401) {
          console.error('Token refresh failed:', refreshError);
        }
        // Don't redirect - let the component handle the error
        // This allows users to stay on password reset pages, etc.
        // Users without refresh tokens can still use the app, they'll just need to log in again
      }
    }

    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const verifyToken = async () => {
    // Don't verify token if we're in the process of logging out
    if (isLoggingOut) {
      setLoading(false);
      return;
    }
    
    const currentPath = window.location.pathname;
    const isPublic = isPublicRoute(currentPath);
    
    try {
      const response = await api.get('/api/auth/verify');
      if (response.status === 200) {
        setUser(response.data);
      }
    } catch (error) {
      // Only set user to null if it's not a public route
      // This prevents clearing user state when on password reset pages
      // On public routes, we just leave user as null (don't clear existing state)
      if (!isPublic) {
        setUser(null);
      }
      // On public routes, silently fail - user might not be logged in, which is fine
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    verifyToken();
  }, []);

  const login = async (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    setIsLoggingOut(true);
    try {
      // Clear user state immediately
      setUser(null);
      
      // Call backend logout
      await api.post('/api/auth/logout');
      
      // Clear any local storage or session storage if needed
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('auth');
      sessionStorage.clear();
      
      // Navigate to login page
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if backend logout fails, clear local state and redirect
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('auth');
      sessionStorage.clear();
      window.location.href = '/';
    } finally {
      setIsLoggingOut(false);
    }
  };

  const hasPermission = (permission) => {
    if (!user || !user.role) return false;
    const rolePermissions = ROLE_PERMISSIONS[user.role];
    return rolePermissions ? rolePermissions[permission] : false;
  };

  const checkMultiplePermissions = (permissions) => {
    return permissions.every(permission => hasPermission(permission));
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        login, 
        logout, 
        loading,
        hasPermission,
        checkMultiplePermissions,
        isAuthenticated: !!user 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Export the configured axios instance for use throughout the app
export { api }; 